import React from "react";
import { gql } from "@apollo/client";
import Table from "@src/lib/ui/Table";
import Text from "@src/lib/ui/Text";
import { Badge, ToneBadgeVariant } from "@src/lib/ui/Badge";
import { Card, CardContent } from "@src/lib/ui/Card";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import { Skeleton } from "@src/lib/ui/Skeleton";
import Spinner from "@src/lib/ui/Spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import TriangleWarningOutlineIcon from "@src/lib/icons/18px/triangle-warning-outline";
import { formatDateTime } from "@src/lib/utils/dates";
import { LocationGroupType } from "@src/types.generated";
import {
  useGuestFeedbackAnalysesQuery,
  GuestFeedbackAnalysisRunListItemFragment,
} from "./GuestFeedbackAnalysesTab.generated";

gql`
  fragment GuestFeedbackAnalysisRunListItem on GuestFeedbackAnalysisRun {
    id
    startedAt
    completedAt
    error
    recommendationCount
    locationGroup {
      id
      name
      type
    }
  }

  query GuestFeedbackAnalyses {
    GuestFeedbackAnalysisRuns {
      ...GuestFeedbackAnalysisRunListItem
    }
  }
`;

// While a run is in flight, refresh so its status and recommendation count
// update without a manual reload.
const ANALYSES_POLL_INTERVAL_MS = 15_000;
const ANALYSIS_LONG_RUNNING_THRESHOLD_MS = 30 * 60 * 1000;
const LONG_RUNNING_ANALYSIS_MESSAGE =
  "This analysis is taking longer than usual. Results will appear here when it finishes.";

type AnalysisRun = GuestFeedbackAnalysisRunListItemFragment;

type AnalysisColumn =
  | "started"
  | "brand"
  | "recommendations"
  | "status"
  | "error";

const ANALYSIS_COLUMNS: AnalysisColumn[] = [
  "started",
  "brand",
  "recommendations",
  "status",
  "error",
];

const ANALYSIS_COLUMN_TITLES: Record<AnalysisColumn, string> = {
  started: "Started",
  brand: "Brand",
  recommendations: "Recommendations",
  status: "Status",
  error: "Error",
};

type RunStatus = "completed" | "failed" | "inProgress";

const RUN_STATUS_DISPLAY: Record<
  RunStatus,
  { label: string; variant: ToneBadgeVariant }
> = {
  completed: { label: "Completed", variant: "toneSuccess" },
  failed: { label: "Failed", variant: "toneError" },
  inProgress: { label: "In progress", variant: "toneNeutral" },
};

// A run record is created when the analysis is dispatched, so a run with
// neither a completion timestamp nor an error is still running.
function getRunStatus(run: AnalysisRun): RunStatus {
  if (run.error) return "failed";
  if (run.completedAt) return "completed";
  return "inProgress";
}

const AnalysisRunStatus: React.FC<{ run: AnalysisRun }> = ({ run }) => {
  const status = getRunStatus(run);
  const statusDisplay = RUN_STATUS_DISPLAY[status];
  const [thresholdReachedFor, setThresholdReachedFor] = React.useState<
    string | null
  >(null);
  const hasExceededThreshold =
    Date.now() - new Date(run.startedAt).getTime() >=
    ANALYSIS_LONG_RUNNING_THRESHOLD_MS;

  React.useEffect(() => {
    if (status !== "inProgress") return;

    const remaining =
      ANALYSIS_LONG_RUNNING_THRESHOLD_MS -
      (Date.now() - new Date(run.startedAt).getTime());
    if (remaining <= 0) {
      setThresholdReachedFor(run.startedAt);
      return;
    }

    const timer = setTimeout(
      () => setThresholdReachedFor(run.startedAt),
      remaining,
    );
    return () => clearTimeout(timer);
  }, [run.startedAt, status]);

  const showLongRunningWarning =
    status === "inProgress" &&
    (hasExceededThreshold || thresholdReachedFor === run.startedAt);

  return (
    <div className="flex items-center gap-2">
      <Badge variant={statusDisplay.variant}>{statusDisplay.label}</Badge>
      {showLongRunningWarning && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={LONG_RUNNING_ANALYSIS_MESSAGE}
                className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-status-warning-bg text-status-warning-text"
              >
                <TriangleWarningOutlineIcon className="size-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {LONG_RUNNING_ANALYSIS_MESSAGE}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

function getRunBrandName(run: AnalysisRun): string | null {
  const locationGroup = run.locationGroup;
  if (locationGroup?.type !== LocationGroupType.Brand) return null;
  return locationGroup.name;
}

const ANALYSES_DESCRIPTION =
  "Opus analyzes new guest feedback for training opportunities as it comes in, up to once a day.";

const GuestFeedbackAnalysesTab: React.FC = () => {
  const { data, loading } = useGuestFeedbackAnalysesQuery({
    pollInterval: ANALYSES_POLL_INTERVAL_MS,
  });

  const runs = data?.GuestFeedbackAnalysisRuns ?? [];

  if (loading && !data) {
    return (
      <div className="flex flex-col gap-3 pb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <Card variant="elevated">
        <CardContent className="p-6">
          <EmptyState>
            <EmptyStateTitle>No analyses yet</EmptyStateTitle>
            <EmptyStateDescription>
              {ANALYSES_DESCRIPTION}
            </EmptyStateDescription>
          </EmptyState>
        </CardContent>
      </Card>
    );
  }

  const showBrand = runs.some((run) => getRunBrandName(run) != null);
  const showError = runs.some((run) => !!run.error);

  return (
    <div className="pb-6">
      <Table<AnalysisRun, AnalysisColumn>
        title="Analyses"
        SubHeader={
          <Text
            variant="compact-description"
            multiline
            className="pt-1 pb-3 text-text-secondary"
          >
            {ANALYSES_DESCRIPTION}
          </Text>
        }
        allItems={runs}
        allColumns={ANALYSIS_COLUMNS}
        visibleColumns={() => {
          const columns: AnalysisColumn[] = ["started"];
          if (showBrand) columns.push("brand");
          columns.push("recommendations", "status");
          if (showError) columns.push("error");
          return columns;
        }}
        itemId={(run) => run.id}
        columnTitles={ANALYSIS_COLUMN_TITLES}
        defaultSort={{ column: "started", descending: true }}
        horizontalScroll
        trailingEmptyColumnWidth={16}
        growableColumn={showError ? "error" : "started"}
        responsiveness={{
          started: { minWidth: 160, width: 200 },
          brand: { minWidth: 120, width: 180 },
          recommendations: { minWidth: 130, width: 160 },
          status: { minWidth: 110, width: 140 },
          error: { minWidth: 200 },
        }}
        sortingFns={{
          started: (run) => Date.parse(run.startedAt),
          brand: (run) => getRunBrandName(run) ?? "",
          recommendations: (run) => run.recommendationCount,
          status: (run) => RUN_STATUS_DISPLAY[getRunStatus(run)].label,
          error: (run) => run.error ?? "",
        }}
        renderFns={{
          started: (run) => (
            <Text variant="compact-description" className="text-text-primary">
              {formatDateTime(new Date(run.startedAt))}
            </Text>
          ),
          brand: (run) => (
            <Text
              variant="compact-description"
              className="text-text-primary"
              ellipsis
              ellipsisParentFlexDirection="row"
            >
              {getRunBrandName(run) ?? "—"}
            </Text>
          ),
          recommendations: (run) =>
            getRunStatus(run) === "inProgress" ? (
              <Spinner size={16} />
            ) : (
              <Text variant="compact-description">
                {run.recommendationCount}
              </Text>
            ),
          status: (run) => {
            return <AnalysisRunStatus run={run} />;
          },
          error: (run) => (
            <Text
              variant="compact-description"
              className="text-text-secondary"
              title={run.error ?? undefined}
              ellipsis
              ellipsisParentFlexDirection="row"
            >
              {run.error ?? "—"}
            </Text>
          ),
        }}
      />
    </div>
  );
};

export default GuestFeedbackAnalysesTab;
